import React, { useMemo, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useDispatch } from 'react-redux'
import {
  Button,
  IconCheckmark,
  IconDocument,
  IconMessages,
  IconSignMinus
} from '@Knowledge-OTP/znk-ui-components'
import { showAlert } from '../../../common/Alert/util'
import './index.css'

const MAX_FILE_SIZE = 10 * 1024 * 1024

const SEND_WHATSAPP_REMINDERS = gql`
  mutation SendWspAlertForMockExam($messages: [MockExamMessageInput!]!) {
    sendWspAlertForMockExam(messages: $messages)
  }
`

const REQUIRED_HEADERS = [
  'Student Name',
  'Student First name',
  'Student Email',
  'Student Phone',
  'Parent Name',
  'Parent First Name',
  'Parent Email',
  'Parent Phone'
]

const normalizeHeader = (header = '') => header.replace(/^\uFEFF/, '').trim().toLowerCase()

const parseCsv = (text) => {
  const rows = []
  let row = []
  let value = ''
  let isQuoted = false

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]

    if (isQuoted) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          value += '"'
          index += 1
        } else {
          isQuoted = false
        }
      } else {
        value += character
      }
      continue
    }

    if (character === '"' && value.length === 0) {
      isQuoted = true
    } else if (character === ',') {
      row.push(value.trim())
      value = ''
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') index += 1
      row.push(value.trim())
      if (row.some(cell => cell.length > 0)) rows.push(row)
      row = []
      value = ''
    } else {
      value += character
    }
  }

  if (isQuoted) throw new Error('The CSV contains an unclosed quoted value.')

  row.push(value.trim())
  if (row.some(cell => cell.length > 0)) rows.push(row)

  return rows
}

const csvToContacts = (text) => {
  const parsedRows = parseCsv(text)
  if (!parsedRows.length) throw new Error('The CSV is empty.')

  const headers = parsedRows[0].map(normalizeHeader)
  const missingHeaders = REQUIRED_HEADERS.filter(
    header => !headers.includes(normalizeHeader(header))
  )

  if (missingHeaders.length) {
    throw new Error(`Missing required columns: ${missingHeaders.join(', ')}.`)
  }

  const indexes = REQUIRED_HEADERS.reduce((result, header) => ({
    ...result,
    [header]: headers.indexOf(normalizeHeader(header))
  }), {})

  const contacts = parsedRows.slice(1).map((cells, index) => {
    const getValue = header => cells[indexes[header]]?.trim() || ''
    const studentPhone = getValue('Student Phone')
    const parentPhone = getValue('Parent Phone')

    return {
      rowNumber: index + 2,
      studentName: getValue('Student Name'),
      studentFirstName: getValue('Student First name'),
      studentEmail: getValue('Student Email'),
      studentPhone,
      parentName: getValue('Parent Name'),
      parentFirstName: getValue('Parent First Name'),
      parentEmail: getValue('Parent Email'),
      parentPhone,
      recipientCount: Number(Boolean(studentPhone)) + Number(Boolean(parentPhone))
    }
  })

  if (!contacts.length) throw new Error('The CSV has headers, but it does not contain any contacts.')

  return contacts
}

const formatFileSize = (size) => {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const SendWhatsAppReminders = () => {
  const dispatch = useDispatch()
  const inputRef = useRef(null)
  const dragDepth = useRef(0)
  const readRequestId = useRef(0)
  const sendInProgress = useRef(false)
  const [sendReminders, { loading: isSending }] = useMutation(SEND_WHATSAPP_REMINDERS)
  const [file, setFile] = useState(null)
  const [contacts, setContacts] = useState([])
  const [error, setError] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [isReading, setIsReading] = useState(false)

  const recipientCount = useMemo(
    () => contacts.reduce((total, contact) => total + contact.recipientCount, 0),
    [contacts]
  )
  const invalidContacts = useMemo(
    () => contacts.filter(contact => contact.recipientCount === 0),
    [contacts]
  )
  const isReady = contacts.length > 0 && invalidContacts.length === 0 && recipientCount > 0

  const resetFile = () => {
    readRequestId.current += 1
    setFile(null)
    setContacts([])
    setError('')
    setIsReading(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const readFile = (selectedFile) => {
    if (sendInProgress.current) return
    setError('')

    if (!selectedFile) return
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      resetFile()
      setError('Please upload a CSV file.')
      return
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      resetFile()
      setError('The file is larger than the 10 MB limit.')
      return
    }

    setFile(selectedFile)
    setContacts([])
    setIsReading(true)
    const currentRequestId = readRequestId.current + 1
    readRequestId.current = currentRequestId

    const reader = new window.FileReader()
    reader.onload = (event) => {
      if (currentRequestId !== readRequestId.current) return
      try {
        setContacts(csvToContacts(event.target.result || ''))
      } catch (parseError) {
        setError(parseError.message)
      } finally {
        setIsReading(false)
      }
    }
    reader.onerror = () => {
      if (currentRequestId !== readRequestId.current) return
      setError('We could not read this file. Please try again.')
      setIsReading(false)
    }
    reader.readAsText(selectedFile)
  }

  const handleInputChange = (event) => {
    const selectedFile = event.target.files?.[0]
    event.target.value = ''
    readFile(selectedFile)
  }

  const handleDragEnter = (event) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current += 1
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current -= 1
    if (dragDepth.current <= 0) {
      dragDepth.current = 0
      setIsDragging(false)
    }
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current = 0
    setIsDragging(false)

    if (sendInProgress.current) return

    if (event.dataTransfer.files.length > 1) {
      resetFile()
      setError('Please upload only one CSV file.')
      return
    }
    readFile(event.dataTransfer.files[0])
  }

  const handleDropZoneKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!sendInProgress.current && inputRef.current) inputRef.current.click()
    }
  }

  const handleSend = async () => {
    if (!isReady || isReading || sendInProgress.current) return

    sendInProgress.current = true
    setError('')

    try {
      const { data } = await sendReminders({ variables: { messages: contacts } })
      if (data?.sendWspAlertForMockExam !== true) {
        throw new Error('We could not submit the WhatsApp reminders. Please try again.')
      }

      resetFile()
      showAlert(
        {
          text: `WhatsApp reminders for ${recipientCount} recipients were queued successfully.`,
          status: 'success'
        },
        dispatch
      )
    } catch (sendError) {
      const text = sendError.message || 'We could not submit the WhatsApp reminders. Please try again.'
      setError(text)
      showAlert({ text, status: 'error' }, dispatch)
    } finally {
      sendInProgress.current = false
    }
  }

  return (
    <div className='whatsapp-reminders-page mt-4 mb-5'>
      <header className='whatsapp-reminders-header'>
        <div className='whatsapp-reminders-title-icon'>
          <IconMessages size={28} color='#696CFF' />
        </div>
        <div>
          <h1>Send WhatsApp reminders</h1>
          <p>Upload a CSV to review student and parent contacts before sending.</p>
        </div>
      </header>

      <section className='whatsapp-reminders-card'>
        <div className='whatsapp-reminders-card-heading'>
          <div>
            <span className='whatsapp-reminders-step'>Step 1</span>
            <h2>Upload contacts</h2>
            <p>Use the same column structure as the reminders export.</p>
          </div>
          <span className='whatsapp-reminders-limit'>CSV · max. 10 MB</span>
        </div>

        <input
          ref={inputRef}
          className='whatsapp-reminders-file-input'
          type='file'
          accept='.csv,text/csv'
          disabled={isSending}
          onChange={handleInputChange}
        />

        <div
          className={`whatsapp-reminders-dropzone${isDragging ? ' is-dragging' : ''}`}
          role='button'
          tabIndex={isSending ? -1 : 0}
          aria-disabled={isSending}
          onClick={() => {
            if (!sendInProgress.current && inputRef.current) inputRef.current.click()
          }}
          onKeyDown={handleDropZoneKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className='whatsapp-reminders-upload-icon'>
            <IconDocument size={30} color='#696CFF' />
          </div>
          <strong>{isDragging ? 'Drop the CSV here' : 'Drag and drop your CSV here'}</strong>
          <span>or <u>browse files</u> from your computer</span>
        </div>

        {file && (
          <div className='whatsapp-reminders-file'>
            <div className='whatsapp-reminders-file-icon'>
              <IconDocument size={20} color='#696CFF' />
            </div>
            <div className='whatsapp-reminders-file-copy'>
              <strong>{file.name}</strong>
              <span>
                {isReading
                  ? 'Reading contacts…'
                  : `${formatFileSize(file.size)} · ${contacts.length} ${contacts.length === 1 ? 'row' : 'rows'}`}
              </span>
            </div>
            <button
              type='button'
              className='whatsapp-reminders-file-remove'
              aria-label='Remove uploaded CSV'
              disabled={isSending}
              onClick={resetFile}
            >
              <IconSignMinus size={14} color='#72809A' />
            </button>
          </div>
        )}

        {error && <div className='whatsapp-reminders-error'>{error}</div>}

        <div className='whatsapp-reminders-columns'>
          <span className='whatsapp-reminders-columns-title'>Required columns</span>
          <div className='whatsapp-reminders-column-list'>
            {REQUIRED_HEADERS.map(header => <span key={header}>{header}</span>)}
          </div>
        </div>
      </section>

      {contacts.length > 0 && (
        <section className='whatsapp-reminders-card whatsapp-reminders-preview'>
          <div className='whatsapp-reminders-card-heading whatsapp-reminders-preview-heading'>
            <div>
              <span className='whatsapp-reminders-step'>Step 2</span>
              <h2>Review recipients</h2>
              <p>Each phone number will receive one reminder.</p>
            </div>
          </div>

          <div className='whatsapp-reminders-summary'>
            <div>
              <span>CSV rows</span>
              <strong>{contacts.length}</strong>
            </div>
            <div>
              <span>WhatsApp recipients</span>
              <strong>{recipientCount}</strong>
            </div>
            <div className={invalidContacts.length ? 'has-issues' : ''}>
              <span>Rows with issues</span>
              <strong>{invalidContacts.length}</strong>
            </div>
          </div>

          {invalidContacts.length > 0 && (
            <div className='whatsapp-reminders-warning'>
              {invalidContacts.length} {invalidContacts.length === 1 ? 'row has' : 'rows have'} no student or parent phone number. Add a phone number before sending.
            </div>
          )}

          <div className='whatsapp-reminders-table-wrap'>
            <table className='whatsapp-reminders-table'>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Student phone</th>
                  <th>Parent</th>
                  <th>Parent phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.rowNumber}>
                    <td>
                      <strong>{contact.studentName || contact.studentFirstName || '—'}</strong>
                      <span>{contact.studentEmail || 'No email'}</span>
                    </td>
                    <td>{contact.studentPhone || '—'}</td>
                    <td>
                      <strong>{contact.parentName || contact.parentFirstName || '—'}</strong>
                      <span>{contact.parentEmail || 'No email'}</span>
                    </td>
                    <td>{contact.parentPhone || '—'}</td>
                    <td>
                      <span className={`whatsapp-reminders-status${contact.recipientCount ? '' : ' is-invalid'}`}>
                        {contact.recipientCount > 0 && <IconCheckmark size={12} color='#2FA36B' />}
                        {contact.recipientCount ? 'Ready' : 'Missing phone'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className='whatsapp-reminders-actions'>
            <div>
              <strong>{isReady ? 'Ready to continue' : 'Review the CSV to continue'}</strong>
              <span>{recipientCount} WhatsApp {recipientCount === 1 ? 'recipient' : 'recipients'}</span>
            </div>
            <Button
              color='primary'
              className='whatsapp-reminders-send'
              label={isSending ? 'Sending WhatsApp reminders…' : 'Send WhatsApp reminders'}
              disabled={!isReady || isReading || isSending}
              activity={isSending}
              onClick={handleSend}
            />
          </footer>
        </section>
      )}
    </div>
  )
}

export default SendWhatsAppReminders
